/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CollapseAllVisibility, IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { Sublist } from 'src/luis/projects/entities/types/Sublist';
import { LuisId } from 'src/luis/types/Resource';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';
import { SublistNormalizedValueCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistNormalizedValueCell';
import { SublistSynonymsCell } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistSynonymsCell';

export type SublistsTableProps = TableProps<SublistStore> & {
    /**
     * A callback for when a sublist is renamed.
     */
    onRenameSublist: (sublistId: LuisId, newName: string) => void;

    /**
     * A callback for when a new sublist is added.
     */
    onAddNewSublist: (name: string) => void;

    /**
     * A callback for when sublist values are added.
     */
    onAddSublistValues: (sublistId: LuisId, values: string[]) => void;

    /**
     * A callback for when sublist values are deleted.
     */
    onDeleteSublistValues: (sublistId: LuisId, values: string[]) => void;

    /**
     * A callback for cancelling table sorting.
     */
    onCancelSorting?: () => void;

    /**
     * User role to determine the actions
     */
    userRole: string;
};

const messages = defineMessages({
    normalizedValueColumnHeader: {
        id: 'listEntityDetails.sublistsTable.normalizedValueColumnHeader',
        defaultMessage: 'Normalized values'
    },
    synonymsColumnHeader: {
        id: 'listEntityDetails.sublistsTable.synonymsColumnHeader',
        defaultMessage: 'Synonyms'
    },
    emptyTablePlaceholder: {
        id: 'listEntityDetails.sublistsTable.emptyTablePlaceholder',
        defaultMessage: "You haven't created any sublists yet"
    }
});

const { getClassName } = getStylistV2('SublistsTable');

const headerWrapperClassName = getClassName('headerWrapperClassName', {
    paddingLeft: '60px',
    borderBottom: '1px solid rgb(244, 244, 244)',
    minHeight: '50px',
    maxWidth: '200px'
});

/**
 * Represents a table of sublists and their values. Contains
 * controls to edit and delete sublist values and rename
 * their normalized form name.
 *
 * @param props The input properties for the sublist table.
 */
export const SublistsTable = (props: SublistsTableProps) => {
    const {
        sortKey,
        items,
        selection,
        onAddNewSublist,
        onRenameSublist,
        onAddSublistValues,
        onDeleteSublistValues,
        onCancelSorting = noop,
        userRole,
        ...rest
    } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    /**
     * Renders a custom header to display the input field to add new utterance.
     */
    const onRenderHeader = () => (
        <SublistNormalizedValueCell
            normalizedValue=""
            onChange={value => {
                onAddNewSublist(value);
                onCancelSorting();
            }}
            className={headerWrapperClassName}
            isDisabled={!canWrite}
        />
    );

    /**
     * Renders the normalized value text field.
     *
     * @param sublist The sublist that is being rendered
     * in this row.
     */
    const onRenderNormalizedValueColumn = (sublist: Sublist): JSX.Element => (
        <SublistNormalizedValueCell
            normalizedValue={sublist.name}
            key={sublist.id}
            onChange={value => onRenameSublist(sublist.id, value)}
            isDisabled={!canWrite}
        />
    );

    /**
     * Renders the synonyms tag container and input field.
     *
     * @param sublist The sublist being rendered
     * in this row.
     */
    const onRenderSynonymsColumn = (sublist: Sublist): JSX.Element => (
        <Stack verticalAlign="center" verticalFill>
            <SublistSynonymsCell
                values={sublist.values}
                onAddValues={values => onAddSublistValues(sublist.id, values)}
                isRemovingDisabled={!canDeleteData}
                onRemoveValues={values => onDeleteSublistValues(sublist.id, values)}
                isEditMode={canWrite}
            />
        </Stack>
    );

    const columns: IColumn[] = [
        {
            key: 'name',
            fieldName: 'name',
            name: localize(messages.normalizedValueColumnHeader),
            minWidth: 150,
            maxWidth: 200,
            isRowHeader: true,
            isResizable: true,
            isCollapsible: false,
            onRender: onRenderNormalizedValueColumn,
            isSorted: sortKey && !!(sortKey.key === 'name'),
            isSortedDescending: sortKey && !!(sortKey.key === 'name') && sortKey?.order === 'desc'
        },
        {
            key: 'values',
            fieldName: 'values',
            name: localize(messages.synonymsColumnHeader),
            minWidth: 300,
            isResizable: true,
            isCollapsible: true,
            onRender: onRenderSynonymsColumn
        }
    ];

    return (
        <Table
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            columns={columns}
            items={items}
            selection={selection}
            groups={[
                {
                    startIndex: 0,
                    name: 'Add sublist',
                    key: 'AddSublistHeader',
                    count: items.length ? items.length : 1,
                    isShowingAll: true
                }
            ]}
            groupProps={{
                collapseAllVisibility: CollapseAllVisibility.hidden,
                onRenderHeader
            }}
            {...rest}
        />
    );
};
